import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserPlus, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";

const Register = () => {
  const { lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("next") || "/account";

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const t = lang === "fr" ? {
    kicker: "Compte Dataleo",
    title: "Créer un compte",
    intro: "Créez votre compte gratuit pour sauvegarder vos favoris et recevoir des synthèses personnalisées.",
    name: "Nom",
    email: "Email professionnel",
    password: "Mot de passe",
    passwordHelp: "8 caractères minimum.",
    submit: "Créer mon compte",
    sending: "Création…",
    ok: "Compte créé. Redirection…",
    hasAccount: "Déjà un compte ?",
    signin: "Se connecter",
    legal: "En créant un compte, vous acceptez notre politique de confidentialité et l'usage transactionnel de votre email.",
  } : {
    kicker: "Dataleo account",
    title: "Create account",
    intro: "Create your free account to save favourites and receive tailored briefings.",
    name: "Name",
    email: "Business email",
    password: "Password",
    passwordHelp: "At least 8 characters.",
    submit: "Create my account",
    sending: "Creating…",
    ok: "Account created. Redirecting…",
    hasAccount: "Already have an account?",
    signin: "Sign in",
    legal: "By creating an account you agree to our privacy policy and to receive transactional emails.",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setOk(false);
    const res = await register({
      email: form.email.trim(),
      password: form.password,
      name: form.name.trim(),
      language: lang,
    });
    if (res.ok) {
      setOk(true);
      setTimeout(() => navigate(redirectTo, { replace: true }), 250);
    } else {
      setError(res.error || (lang === "fr" ? "Erreur lors de la création du compte." : "Could not create the account."));
    }
    setBusy(false);
  };

  return (
    <div data-testid="register-page">
      <SeoHead lang={lang} path="/register" title={t.title} description={t.intro} noindex />
      <PageHero
        kicker={t.kicker}
        title={t.title}
        description={t.intro}
        accent="green"
        compact
        testId="register-hero"
      />
      <div className="dl-container py-10 max-w-md">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.name}</span>
          <input
            data-testid="register-name"
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.email} *</span>
          <input
            data-testid="register-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.password} *</span>
          <input
            data-testid="register-password"
            type="password"
            required
            autoComplete="new-password"
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
          <span className="text-[11px] text-[#9ca3af]">{t.passwordHelp}</span>
        </label>

        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-[#0f172a] disabled:opacity-60 text-white px-5 py-3 text-sm font-bold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} {busy ? t.sending : t.submit}
        </button>

        <p className="text-[11px] text-[#9ca3af]">{t.legal}</p>

        {error && (
          <div data-testid="register-error" className="inline-flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        {ok && (
          <div data-testid="register-ok" className="inline-flex items-center gap-2 text-sm text-[#00A878]">
            <CheckCircle2 size={16} /> {t.ok}
          </div>
        )}
      </form>

      <div className="mt-6 text-sm text-[#4B5563]">
        {t.hasAccount}{" "}
        <Link data-testid="register-login-link" to={`/login${redirectTo && redirectTo !== "/account" ? `?next=${encodeURIComponent(redirectTo)}` : ""}`} className="font-semibold text-[#111827] hover:text-[#00A878]">
          {t.signin}
        </Link>
      </div>
      </div>
    </div>
  );
};

export default Register;
